import type {
    ApplicationSettingTheme,
    DataSourceAbstract,
    FieldBlockAbstract,
    FieldBlockConfig,
    FieldBreakPointConfigProtocol,
    FieldBreakPointProtocol,
    RelativeSelectConfig,
    SizeConfigure
} from '@lighthouse/core'
import {
    ALIGNMENT,
    CALENDAR_MODE,
    CALENDAR_TIME_ZONE,
    CheckboxShapeEnum,
    DIRECTION,
    FIELD_TYPE_LIMIT_ENUM,
    type FieldInputConfigProtocol,
    type FieldInputType,
    type FieldOptionsProtocol
} from '@lighthouse/core'
import {
    DATE_FORMAT_OPTIONS,
    FieldTypeToInputTypeMap,
    getCanEditFieldIds,
    getFieldOptions,
    TEXT_FONT_NORMAL,
    themeFieldDesignConfig
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { mergeDeepRight, omit, reduce } from 'rambda'

const FIELD_INPUT_STYLES: FieldOptionsProtocol = {
    canEdit: true,
    showTitle: true,
    size: 'middle',
    direction: 'column'
}

/**
 * 生成字段block的配置
 *
 * @param {FieldInputType} type
 * @returns {FieldInputConfigProtocol}
 */
export const generateFieldBlock = (mainBreakPoint: string, type: FieldInputType, theme?: ApplicationSettingTheme) => {
    function getFieldConfig(type: FieldInputType): FieldInputConfigProtocol {
        switch (type) {
            case 'text':
            case 'url':
            case 'phoneNumber':
            case 'email': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请输入'
                }
            }
            case 'file': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请输入',
                    file: {
                        mode: 'list',
                        fileLimitType: FIELD_TYPE_LIMIT_ENUM.ALL
                    }
                }
            }
            case 'notes': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请输入'
                }
            }
            case 'checkbox': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    checkbox: {
                        gapType: 'fixed',
                        gap: 0.625,
                        checkedIcon: {
                            icon: {
                                type: 'icon',
                                value: 'AltArrowUpLinear'
                            },
                            color: { type: 'color', color: '#98A2B3' }
                        },
                        unCheckedIcon: {
                            icon: {
                                type: 'icon',
                                value: 'AltArrowDownLinear'
                            },
                            color: { type: 'color', color: '#98A2B3' }
                        },
                        shape: CheckboxShapeEnum.checkbox,
                        type: 'checkbox',
                        describePosition: 'right'
                    }
                }
            }
            case 'date': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请选择',
                    date: {
                        dateFormat: DATE_FORMAT_OPTIONS[0].value,
                        range: 'ALL'
                    }
                }
            }
            case 'number': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请输入',
                    number: {}
                }
            }
            // case 'select': {
            //     return {
            //         inputType: type,
            //         placeholder: '请选择',
            //         ...FIELD_INPUT_STYLES,
            //         select: {
            //             canMultipleChoice: false
            //         }
            //     }
            // }
            case 'person': {
                return {
                    inputType: type,
                    placeholder: '请选择',
                    ...FIELD_INPUT_STYLES,
                    person: {
                        canMultipleChoice: false,
                        filter: {}
                    }
                }
            }
            case 'relativeSelect': {
                return {
                    inputType: type,
                    placeholder: '请选择',
                    ...FIELD_INPUT_STYLES,
                    relativeSelect: {
                        align: ALIGNMENT.left,
                        showType: 'table',
                        viewFieldSettings: [],
                        direction: 'vertical',
                        showMode: 'input',
                        addButton: {
                            enable: false,
                            iconPosition: 'left',
                            name: '添加',
                            showType: 'icon-name',
                            icon: 'AddCircleLinear',
                            iconColor: {
                                type: 'color',
                                color: theme?.primaryPointer
                            },
                            color: {
                                type: 'color',
                                color: theme?.primaryPointer
                            },
                            eventList: []
                        }
                    }
                }
            }
            case 'slider': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请输入',
                    slider: {
                        min: 0,
                        max: 100,
                        step: 1,
                        isInput: true
                    }
                }
            }
            case 'cascade': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    placeholder: '请选择',
                    cascade: {
                        cascadePointer: '',
                        cascadeFieldPointer: 'ID',
                        cascadeShowFieldPointer: '',
                        parentFieldPointer: '',
                        sortFieldPointer: '',
                        isLastLevel: true,
                        showPath: true
                    }
                }
            }
            case 'calendar': {
                return {
                    inputType: type,
                    ...FIELD_INPUT_STYLES,
                    showTitle: false,
                    placeholder: '请选择',
                    calendar: {
                        mode: CALENDAR_MODE.week,
                        timeZone: CALENDAR_TIME_ZONE.local,
                        dateFormat: DATE_FORMAT_OPTIONS[3].value,
                        range: 'ALL',
                        // 可选日期 所在数据表
                        rangeDsId: '',
                        // 可选日期 所在数据表字段
                        rangeFieldId: '',
                        // 对应日期描述  匹配对应数据表
                        describeDsId: '',
                        // 对应日期描述 匹配的对应日期字段
                        describeFieldId: '',
                        // 依据日期描述 生成对应描述字段
                        describeTextFieldId: '',
                        enabled: false
                    }
                }
            }
            default: {
                return { inputType: 'text' }
            }
        }
    }

    const minHeightConfigure =
        type === 'notes'
            ? {
                  minHeight: {
                      size: 300,
                      unit: 'px'
                  } as SizeConfigure
              }
            : undefined

    const designSetting = {
        ...themeFieldDesignConfig(type, { theme }).LIGHT,
        design: {
            ...themeFieldDesignConfig(type, { theme }).LIGHT?.design,
            theme: 'LIGHT'
        }
    } as Pick<
        FieldBreakPointConfigProtocol,
        'design' | 'fileDesign' | 'personDesign' | 'selectDesign' | 'sliderDesign' | 'hoverStyle' | 'activeStyle' | 'disabledStyle'
    >

    const calendarSetting: Pick<FieldBreakPointConfigProtocol, 'calendarHeader' | 'calendarWeek' | 'calendarDate' | 'calendar'> = {
        calendarHeader: {
            fontFamily: TEXT_FONT_NORMAL,
            fontColor: {
                type: 'color',
                color: '#101828'
            },
            iconColor: {
                type: 'color',
                color: '#98A2B3'
            }
        },
        calendarWeek: {
            fontFamily: TEXT_FONT_NORMAL,
            fontColor: {
                type: 'color',
                color: '#98A2B3'
            }
        },
        calendarDate: {
            fontFamily: TEXT_FONT_NORMAL,
            // 当前月文字颜色
            currentMonthFontColor: {
                type: 'color',
                color: '#101828'
            },
            notCurrentMonthFontColor: {
                type: 'color',
                color: '#98A2B3'
            },
            selectedFontColor: {
                type: 'color',
                color: '#FFFFFF'
            },
            selectedBackgroundColor: {
                type: 'color',
                color: theme?.primaryPointer
            },
            hoverFontColor: {
                type: 'color',
                color: '#101828'
            },
            hoverBackgroundColor: {
                type: 'color',
                color: '#ECEFF4'
            }
        },
        calendar: {
            backgroundColor: {
                type: 'color',
                color: '#ffffff'
            },
            describeFontColor: {
                type: 'color',
                color: '#475467'
            }
        }
    }

    const breakPointConfig: FieldBreakPointProtocol = {
        breakPoint: {
            id: mainBreakPoint,
            visibility: {
                visible: 'VISIBLE'
            },
            position: {
                type: 'relative'
            },
            font: {
                fontFamily: TEXT_FONT_NORMAL
            },
            ...designSetting,
            // design: {
            //     theme: 'LIGHT',
            //     ...themeFieldDesignConfig(type).LIGHT
            // },
            size: {
                width: {
                    size: 'fill'
                },
                height: {
                    size: type === 'calendar' ? 80 : 'auto'
                },
                ...minHeightConfigure,
                overflow: 'visible'
            },
            ...calendarSetting
            // 是否开启依据日期字段生成日期描述 参数
        },
        breakPoints: []
    }

    return {
        ...getFieldConfig(type),
        ...(type === 'calendar' ? breakPointConfig : omit(['calendarHeader', 'calendarWeek', 'calendarDate', 'calendar'], breakPointConfig))
    } as FieldBlockConfig
}

/**
 * 需要额外创建至多5个上层数据源对应的非系统表单字段
 * lookup、formula除外
 * 置入创建的表单容器内
 * @param dataSource
 */
export function padBlocksByFormContainer(
    mainBreakPoint: string,
    dataSource: DataSourceAbstract,
    dataSourceList: DataSourceAbstract[],
    theme: ApplicationSettingTheme
) {
    // 过滤系统字段
    const { schema, viewOptions, type } = dataSource || {}
    const { tableProps = [] } = viewOptions || {}
    const noSystemFields = getCanEditFieldIds(schema, viewOptions, type)
    const fieldOptionIds = getFieldOptions(schema, tableProps, field => noSystemFields.includes(field.id)).map(v => v.value)
    // 筛选前五个字段并生成block
    const fieldBlocks = reduce<string, FieldBlockAbstract[]>(
        (pre, cur) => {
            const field = schema?.[cur]
            const inputType = FieldTypeToInputTypeMap[field.type]
            if (!inputType || !field) {
                return pre
            }
            const config = generateFieldBlock(mainBreakPoint, inputType, theme)
            config.fieldPointer = field.id
            config.canDownload = false
            config.canEdit = true
            config.direction = 'column'
            config.showTitle = true
            config.required = false
            config.noRepeat = false
            config.title = field.name

            switch (config.inputType) {
                case 'date': {
                    config.placeholder = '请选择'
                    break
                }

                case 'number': {
                    config.placeholder = '请输入'
                    config.number = mergeDeepRight(config.number || {}, {
                        mode: 'number',
                        accuracy: 0,
                        prefix: '',
                        suffix: ''
                    })
                    break
                }

                case 'phoneNumber':
                case 'text':
                case 'url':
                case 'email':
                case 'file':
                case 'notes': {
                    config.placeholder = '请输入'
                    break
                }

                case 'person': {
                    config.placeholder = '请选择'
                    config.person = {
                        canMultipleChoice: false,
                        filter: {}
                    }
                    break
                }

                case 'cascade': {
                    config.placeholder = '请选择'
                    break
                }
                case 'relativeSelect': {
                    config.placeholder = '请选择'
                    if (field.type === 'singleSelect' || field.type === 'select') {
                        break
                    }
                    if (field.type === 'role' && config.relativeSelect) {
                        config.relativeSelect = mergeDeepRight<RelativeSelectConfig['relativeSelect']>(config.relativeSelect, {
                            canMultipleChoice: false,
                            relativePointer: 'ROLE_DATASOURCE',
                            relativeFieldPointer: 'ID',
                            viewFieldSettings: [],
                            sorts: [],
                            relativeShowFieldPointer: 'ROLE_NAME',
                            showType: 'list'
                        })
                        break
                    }
                    config.relativeSelect = mergeDeepRight(config.relativeSelect || {}, {
                        align: ALIGNMENT.left,
                        canMultipleChoice: false,
                        direction: DIRECTION.vertical,
                        filter: {},
                        showType: 'table',
                        sorts: [],
                        viewFieldSettings: []
                    } satisfies typeof config.relativeSelect)
                    break
                }

                default: {
                    break
                }
            }

            return [
                ...pre,
                {
                    id: `field-${nanoid(12)}`,
                    type: 'field',
                    title: field.name,
                    config
                }
            ]
        },
        [],
        fieldOptionIds
    ).slice(0, 5)

    return [...fieldBlocks]
}
